import axios from 'axios'
import type { AxiosInstance } from 'axios'
import { ConfigService } from '@/services/ConfigService'
import { ActiveTenantService } from '@/services/ActiveTenantService'
import { SESSION_GRIDS_KEY } from '@/services/sessionStorageKeys'
import { isCancellation } from '@/utils/errorUtils'
import { allowUnload } from '@/utils/pageUnload'

declare module 'axios' {
  interface AxiosRequestConfig {
    suppressForbiddenRedirect?: boolean
  }
}

const DEFAULT_API_TIMEOUT_MS = 15000
const configuredTimeout = Number(import.meta.env.VITE_API_TIMEOUT_MS)
const apiTimeout = Number.isFinite(configuredTimeout) && configuredTimeout > 0
  ? configuredTimeout
  : DEFAULT_API_TIMEOUT_MS

function isSessionExpired(): boolean {
  const expireRaw = sessionStorage.getItem('sessionExpire')
  return !!expireRaw && Date.now() >= new Date(expireRaw).getTime()
}

function redirectToLogin(message: string): void {
  sessionStorage.setItem('authError', message)
  sessionStorage.removeItem('sessionToken')
  sessionStorage.removeItem('sessionExpire')
  // Session-scoped grid filters must not survive into the next sign-in on this tab (PR-797).
  sessionStorage.removeItem(SESSION_GRIDS_KEY)
  allowUnload()
  window.location.href = '/login'
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: ConfigService.apiUrl ?? '/',
  timeout: apiTimeout,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

// Attach session token and the active tenant to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('sessionToken')
    if (token) config.headers.Authorization = `ECXBearer ${token}`
    const activeTenant = ActiveTenantService.activeTenant.value
    if (activeTenant) config.headers['X-Tenant'] = activeTenant
    return config
  },
  (error) => Promise.reject(error),
)

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Cancelled requests are not errors worth surfacing
    if (isCancellation(error)) return Promise.reject(error)

    // Scrub bearer token before the error reaches any logger or telemetry sink
    if (error?.config?.headers) delete error.config.headers['Authorization']

    const status = error?.response?.status

    // 401 — session expired or invalid token (skip on /login; wrong-password 401 is handled there)
    if (status === 401 && !window.location.pathname.startsWith('/login')) {
      redirectToLogin('Your session has expired. Please sign in again.')
      return Promise.reject(error)
    }

    // 403 — session-expired or bootstrap: redirect to login.
    // Legitimate permission denial after bootstrap: navigate to /forbidden.
    if (status === 403 && sessionStorage.getItem('sessionToken')) {
      if (isSessionExpired()) {
        redirectToLogin('Your session has expired. Please sign in again.')
      } else if (error?.config?.suppressForbiddenRedirect) {
        return Promise.reject(error)
      } else if (!window.location.pathname.endsWith('/forbidden')) {
        // Guard against a hard-redirect loop: once on /forbidden, re-bootstrap 403s (e.g. a role-less impersonation target) must not re-navigate (#5076).
        window.location.href = '/forbidden'
      }
      return Promise.reject(error)
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
